// Staff earnings review + pay-period ledger hooks. Reads register under
// `earningsKeys.all`; every review/finalize mutation invalidates that prefix so
// the summary, grade, pay-period, and ledger reads refetch (DISPLAY-ONLY — no
// money moves in the system).

import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  earningsApi,
  type LeadGenManualAdjustRequest,
} from "@/api/leadGen/earnings";
import { earningsKeys } from "@/hooks/leadGen/earningsKeys";

export function useEarningsSummary(leadGeneratorId: string | null, weekOf: string) {
  return useQuery({
    queryKey: earningsKeys.summary(leadGeneratorId ?? "", weekOf),
    queryFn: () => earningsApi.summary(leadGeneratorId as string, weekOf),
    enabled: !!leadGeneratorId && !!weekOf,
  });
}

/** The AUTOMATIC grade + bonus multiplier + approvable figures for a generator's
 *  pay week (read-only — grade is computed server-side, never set by a manager). */
export function useGrade(leadGeneratorId: string | null, weekOf: string) {
  return useQuery({
    queryKey: earningsKeys.grade(leadGeneratorId ?? "", weekOf),
    queryFn: () => earningsApi.grade(leadGeneratorId as string, weekOf),
    enabled: !!leadGeneratorId && !!weekOf,
  });
}

/** Fan-out weekly summaries for the cross-generator oversight board. Each query
 *  registers under the same `earningsKeys.summary` key a per-generator read uses,
 *  so a review mutation's prefix invalidation refreshes the oversight row too. */
export function useGeneratorWeekSummaries(generatorIds: string[], weekOf: string) {
  return useQueries({
    queries: generatorIds.map((id) => ({
      queryKey: earningsKeys.summary(id, weekOf),
      queryFn: () => earningsApi.summary(id, weekOf),
      enabled: !!id && !!weekOf,
    })),
  });
}

export function usePayPeriods(leadGeneratorId: string | null) {
  return useQuery({
    queryKey: earningsKeys.payPeriods(leadGeneratorId ?? ""),
    queryFn: () => earningsApi.payPeriods(leadGeneratorId as string),
    enabled: !!leadGeneratorId,
  });
}

/** Record a signed-dollar adjustment on the generator's current OPEN week. */
export function useAddAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LeadGenManualAdjustRequest) => earningsApi.adjust(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: earningsKeys.all });
    },
  });
}

export function useApprovePayPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { leadGeneratorId: string; weekOf: string }) =>
      earningsApi.approvePayPeriod(vars.leadGeneratorId, vars.weekOf),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: earningsKeys.all });
    },
  });
}

export function useLockPayPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { leadGeneratorId: string; weekOf: string; lock: boolean }) =>
      vars.lock
        ? earningsApi.lockPayPeriod(vars.leadGeneratorId, vars.weekOf)
        : earningsApi.unlockPayPeriod(vars.leadGeneratorId, vars.weekOf),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: earningsKeys.all });
    },
  });
}

// --- Finalized pay-period ledger --------------------------------------------

export function useLedgerHistory(leadGeneratorId: string | null) {
  return useQuery({
    queryKey: earningsKeys.ledgerHistory(leadGeneratorId ?? ""),
    queryFn: () => earningsApi.ledgerHistory(leadGeneratorId as string),
    enabled: !!leadGeneratorId,
  });
}

export function useLedgerWeek(weekOf: string) {
  return useQuery({
    queryKey: earningsKeys.ledgerWeek(weekOf),
    queryFn: () => earningsApi.ledgerWeek(weekOf),
    enabled: !!weekOf,
  });
}

export function useLedgerWeekLineItems(leadGeneratorId: string | null, weekOf: string | null) {
  return useQuery({
    queryKey: earningsKeys.ledgerWeekLineItems(leadGeneratorId ?? "", weekOf ?? ""),
    queryFn: () => earningsApi.ledgerWeekLineItems(leadGeneratorId as string, weekOf as string),
    enabled: !!leadGeneratorId && !!weekOf,
  });
}
